import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../Management/Campaign.css";
import nexticn from "../Components/Assets/svg/chevron-right-solid.svg";
import backicn from "../Components/Assets/svg/back-chevron-right-solid (1).svg";
import { toast, ToastContainer, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import viewicn from "../Components/Assets/Campaign/eye.png";

// Helper function to get tomorrow's date
const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split("T")[0];
};

// Helper function to get date with specified days added
const getDateWithDaysAdded = (baseDate, days) => {
  const date = new Date(baseDate);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

export function Campaign() {
  const navigate = useNavigate();
  const [isCustomType, setIsCustomType] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const campaignScrollRef = useRef(null);
  const [showDetailEditor, setShowDetailEditor] = useState(false); // popup for subject/body
  const [campaignSteps, setCampaignSteps] = useState([
    { type: "Initial", date: "", subject: "", body: "", attachmentFile: null },
  ]);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [previewRowIndex, setPreviewRowIndex] = useState(null);
  const [editorForm, setEditorForm] = useState({ subject: "", body: "" });
  const [startDateInputError, setStartDateInputError] = useState(false); // Track start date input error state
  // Form state
  const [campaignForm, setCampaignForm] = useState({
    name: "",
    audience: "Customers",
    type: "Initial",
    customType: "",
    date: "",
    customDate: "",
    subject: "",
    body: "",
    campaignCategory: "Recurring",
    startDate: "",
    endDate: "",
  });
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companyError, setCompanyError] = useState("");
  const [campaignSubmitStatus, setCampaignSubmitStatus] = useState("idle");
  const [fieldErrors, setFieldErrors] = useState({}); // NEW
  // Company profile ID (after fetch / localStorage)
  const companyProfileId =
    companyProfile?._id || localStorage.getItem("companyProfileId");
  console.log("Using companyProfileId:", companyProfileId);

  // Campaign list from API
  const [campaigns, setCampaigns] = useState([]);

  // Use standardized API URL (same as task/content management flow)
  const API_BASE = process.env.REACT_APP_API_URL;

  // Validate token + fetch company profile (mirrors ContentManagement logic)
  useEffect(() => {
    const init = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setCompanyError("Authentication required");
          setIsLoading(false);
          navigate("/login");
          return;
        }

        // Optional token validation endpoint (removed to avoid clearing valid token if /me not deployed)
        /*
        try {
          const meRes = await fetch(`${API_BASE}/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!meRes.ok) {
            localStorage.removeItem("token");
            navigate("/login");
            return;
          }
        } catch (_) {}
        */

        // Always fetch a fresh company profile
        const cpRes = await fetch(`${API_BASE}/company/get-company-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cpRes.ok) {
          const data = await cpRes.json();
          if (data && data._id) {
            setCompanyProfile(data);
            localStorage.setItem("companyProfileId", data._id);
            console.log("Fresh company profile fetched:", data._id);
          } else {
            setCompanyError("Company profile not found");
          }
        } else if (cpRes.status === 404) {
          setCompanyError("Please create a company profile first");
          navigate("/new-company");
        } else {
          setCompanyError("Failed to load company profile");
        }
      } catch (err) {
        console.error("Company profile init error", err);
        setCompanyError("Initialization error");
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [API_BASE, navigate]);

  const fetchCampaigns = async () => {
    if (!companyProfileId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE}/campaign/company/${companyProfileId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined, // add header only if token exists
        }
      );

      if (res.status === 401) {
        toast.error("Session expired. Please login.");
        localStorage.removeItem("token");
        setTimeout(() => navigate("/login"), 1000);
        return;
      }
      if (!res.ok) throw new Error("Failed to load campaigns");
      const data = await res.json();
      setCampaigns(data);
    } catch (e) {
      console.error("Fetch campaigns error", e);
      toast.error("Unable to load campaigns");
    }
  };

  useEffect(() => {
    if (companyProfileId) fetchCampaigns();
  }, [companyProfileId]);

  // Debug: Monitor previewRowIndex state changes
  useEffect(() => {
    console.log("previewRowIndex changed to:", previewRowIndex);
  }, [previewRowIndex]);

  const handleCustomTypeChange = (e) => {
    setIsCustomType(e.target.checked);
  };

  const handleCreateCampaignClick = () => {
    const willOpen = !showCreateForm;
    setShowCreateForm(willOpen);

    // Common resets whenever the button is clicked
    setPreviewRowIndex(null);
    setEditingRowIndex(null);
    setShowDetailEditor(false);
    setFieldErrors({});
    setStartDateInputError(false);
    setCampaignSubmitStatus("idle");
    setIsCustomType(false);

    // If opening create form, ensure no campaign is selected
    if (willOpen) {
      setSelectedCampaign(null);
    }

    // Reset form, steps, and editor to defaults
    setCampaignForm({
      name: "",
      audience: "Customers",
      type: "Initial",
      customType: "",
      date: "",
      customDate: "",
      subject: "",
      body: "",
      campaignCategory: "Recurring",
      startDate: "",
      endDate: "",
    });
    setCampaignSteps([
      { type: "Initial", date: "", subject: "", body: "", attachmentFile: null },
    ]);
    setEditorForm({ subject: "", body: "" });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "campaignCategory") {
      setCampaignForm((prev) => ({
        ...prev,
        [name]: value,
        ...(value === "Special Event"
          ? { startDate: "", endDate: "" } // clear recurring dates when switching to Special Event
          : {}),
      }));
      if (value === "Recurring" && campaignSteps.length > 1) {
        setCampaignSteps((prev) => [prev[0]]);
      }
      if (value === "Special Event") {
        // remove any previous date errors
        setFieldErrors((prev) => {
          const n = { ...prev };
          delete n.startDate;
          delete n.endDate;
          return n;
        });
      }
      return;
    }

    // Handle start date changes with validation
    if (name === "startDate") {
      setCampaignForm((prev) => {
        const newState = { ...prev, [name]: value };

        // If end date exists and start date is changed, validate end date
        if (prev.endDate && value) {
          const startDate = new Date(value);
          const endDate = new Date(prev.endDate);

          // Calculate minimum end date (start date + 6 days)
          const minEndDate = new Date(startDate);
          minEndDate.setDate(minEndDate.getDate() + 6);

          // If end date is before minimum allowed date, clear end date
          if (endDate < minEndDate) {
            newState.endDate = "";
          }
        }

        return newState;
      });

      // Clear start date input error when user selects a date
      if (value) {
        setStartDateInputError(false);
      }

      return;
    }

    // Handle end date changes with validation
    if (name === "endDate") {
      // Check if start date is selected before allowing end date selection
      if (!campaignForm.startDate) {
        setStartDateInputError(true);
        toast.error("Please select start date first", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        return;
      }

      // Validate 6-day gap
      if (value && campaignForm.startDate) {
        const startDate = new Date(campaignForm.startDate);
        const endDate = new Date(value);
        const daysDifference = Math.ceil(
          (endDate - startDate) / (1000 * 60 * 60 * 24)
        );

        if (daysDifference < 6) {
          toast.error("End date must be at least 6 days after start date", {
            className: "gradient-toast",
            bodyClassName: "gradient-toast-body",
            progressClassName: "gradient-toast-progress",
          });
          return;
        }
      }
    }

    setCampaignForm((prev) => ({ ...prev, [name]: value }));
  };

  const computeErrors = () => {
    const errs = {};
    if (!campaignForm.name.trim()) errs.name = true;

    if (campaignForm.campaignCategory === "Recurring") {
      const first = campaignSteps[0];
      if (!campaignForm.startDate) errs.startDate = true;
      if (!campaignForm.endDate) errs.endDate = true;
      if (!first.type) errs[`step0_type`] = true;
      if (!first.subject?.trim()) errs[`step0_subject`] = true;
      if (!first.body?.trim()) errs[`step0_body`] = true;
    } else {
      campaignSteps.forEach((s, i) => {
        if (!s.type) errs[`step${i}_type`] = true;
        if (!s.date) errs[`step${i}_date`] = true;
        if (!s.subject?.trim()) errs[`step${i}_subject`] = true;
        if (!s.body?.trim()) errs[`step${i}_body`] = true;
      });
    }
    return errs;
  };

  // ADD missing allowedTypes (used by handleAddStep / availableTypesForRow)
  const allowedTypes = [
    "Initial",
    "1st Follow Up",
    "2nd Follow Up",
    "3rd Follow Up",
    "4th Follow Up",
    "Final",
    "Closing",
    "Thank You Notes",
  ];

  const handleSubmitCampaign = async (e) => {
    e.preventDefault();
    if (campaignSubmitStatus === "submitting") return;
    const errs = computeErrors();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (!companyProfileId) {
      toast.error("Select or load a company profile first.");
      return;
    }
    if (!campaignForm.name) {
      toast.error("Campaign name required");
      return;
    }
    if (campaignSteps.length === 0) {
      toast.error("Add at least one campaign step.");
      return;
    }
    if (campaignForm.campaignCategory === "Recurring") {
      const first = campaignSteps[0];
      if (!campaignForm.startDate || !campaignForm.endDate) {
        toast.error("Recurring campaigns require Start Date and End Date.");
        return;
      }
      if (!first.type || !first.subject || !first.body) {
        toast.error("Recurring campaign step requires Type, Subject and Body.");
        return;
      }
    } else {
      for (let i = 0; i < campaignSteps.length; i++) {
        const s = campaignSteps[i];
        if (!s.type || !s.date || !s.subject || !s.body) {
          toast.error(`Step ${i + 1} incomplete.`);
          return;
        }
      }
    }

    try {
      setCampaignSubmitStatus("submitting");
      const formData = new FormData();
      formData.append("companyProfileId", companyProfileId);
      formData.append("name", campaignForm.name);
      formData.append("audience", campaignForm.audience);
      formData.append("campaignCategory", campaignForm.campaignCategory);
      if (campaignForm.campaignCategory === "Recurring") {
        formData.append("startDate", campaignForm.startDate || "");
        formData.append("endDate", campaignForm.endDate || "");
      } else {
        formData.append("startDate", "");
        formData.append("endDate", "");
      }
      const stepsMeta = campaignSteps.map((s) => ({
        type: s.type,
        date: s.date || "",
        subject: s.subject || "",
        body: s.body || "",
      }));
      formData.append("stepsJson", JSON.stringify(stepsMeta));
      campaignSteps.forEach((s, idx) => {
        if (s.attachmentFile)
          formData.append(`stepAttachment_${idx}`, s.attachmentFile);
      });

      const token = localStorage.getItem("token"); // MAY be null (we allow anonymous create if server permits)
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_BASE}/campaign`, true);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`); // re-add if server requires

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            toast.success("Campaign created successfully!");
            setCampaignSubmitStatus("submitted");
            setTimeout(() => {
              setCampaignSubmitStatus("idle");
              setShowCreateForm(false);
              setPreviewRowIndex(null); // Reset preview state
            }, 1200);
            setCampaignForm({
              name: "",
              audience: "Customers",
              type: "Initial",
              customType: "",
              date: "",
              customDate: "",
              subject: "",
              body: "",
              campaignCategory: "Recurring",
              startDate: "",
              endDate: "",
            });
            setCampaignSteps([
              {
                type: "Initial",
                date: "",
                subject: "",
                body: "",
                attachmentFile: null,
              },
            ]);
            fetchCampaigns();
          } else {
            // Detect auth failure
            if (
              xhr.status === 401 ||
              /No token/i.test(xhr.responseText || "")
            ) {
              toast.error("Authorization required. Please login again.");
              localStorage.removeItem("token");
              setCampaignSubmitStatus("idle");
              setTimeout(() => navigate("/login"), 1200);
              return;
            }
            let msg = "Campaign creation failed";
            try {
              const parsed = JSON.parse(xhr.responseText || "{}");
              if (parsed.message) msg = parsed.message;
            } catch (_) {}
            toast.error(msg);
            setCampaignSubmitStatus("idle");
          }
        }
      };
      xhr.onerror = () => {
        toast.error("Network error.");
        setCampaignSubmitStatus("idle");
      };
      xhr.send(formData);
    } catch (err) {
      console.error("Campaign create error", err);
      toast.error("Failed to create campaign");
      setCampaignSubmitStatus("idle");
    }
  };

  const handleOpenDetailEditor = (idx) => {
    setEditingRowIndex(idx);
    const step = campaignSteps[idx];
    setEditorForm({ subject: step.subject || "", body: step.body || "" });
    setShowDetailEditor(true);
  };

  const handleSaveDetailEditor = () => {
    if (editingRowIndex === null) return;
    const currentEditingIndex = editingRowIndex; // Store current index before reset
    setCampaignSteps((prev) =>
      prev.map((s, i) =>
        i === currentEditingIndex
          ? {
              ...s,
              subject: editorForm.subject,
              body: editorForm.body,
              // attachmentFile already captured via input onChange
            }
          : s
      )
    );
    if (currentEditingIndex === 0) {
      setCampaignForm((prev) => ({
        ...prev,
        subject: editorForm.subject,
        body: editorForm.body,
      }));
    }
    setShowDetailEditor(false);
    setEditingRowIndex(null);
    toast.success("Details saved (not yet submitted)");

    // Force re-render to update button text and enable preview
    setTimeout(() => {
      console.log("Details saved for step", currentEditingIndex);
    }, 0);
  };

  const handleCancelDetailEditor = () => {
    setShowDetailEditor(false);
    setEditingRowIndex(null);
  };

  const handleOpenDetailPreview = (idx) => {
    console.log("Opening preview for index:", idx); // Debug log
    console.log("Current previewRowIndex before:", previewRowIndex);
    console.log("Selected campaign:", selectedCampaign ? "exists" : "null");
    console.log("Campaign steps:", campaignSteps[idx]); // Debug campaign steps
    setPreviewRowIndex(idx);
    console.log("Setting previewRowIndex to:", idx);
    // Force re-render
    setTimeout(() => {
      console.log("After timeout, previewRowIndex is:", previewRowIndex);
    }, 100);
  };

  const handleCloseDetailPreview = () => {
    console.log("Closing preview"); // Debug log
    console.log("Current previewRowIndex before:", previewRowIndex);
    console.log("Selected campaign:", selectedCampaign ? "exists" : "null");
    setPreviewRowIndex(null);
    console.log("Setting previewRowIndex to: null");
    // Force re-render
    setTimeout(() => {
      console.log("After timeout, previewRowIndex is:", previewRowIndex);
    }, 100);
  };

  const handleStepTypeChange = (idx, value) => {
    setCampaignSteps((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, type: value } : s))
    );
    if (idx === 0) setCampaignForm((prev) => ({ ...prev, type: value }));
  };

  const handleStepDateChange = (idx, value) => {
    setCampaignSteps((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, date: value } : s))
    );
    if (idx === 0) setCampaignForm((prev) => ({ ...prev, date: value }));
  };

  const handleAddStep = () => {
    if (campaignForm.campaignCategory === "Recurring") return; // disabled for recurring now
    if (campaignSteps.length >= allowedTypes.length) return;
    // Determine remaining types
    const used = campaignSteps.map((s) => s.type);
    const nextType = allowedTypes.find((t) => !used.includes(t)) || "";
    setCampaignSteps((prev) => [
      ...prev,
      { type: nextType, date: "", subject: "", body: "" },
    ]);
  };

  const handleRemoveStep = (idx) => {
    setCampaignSteps((prev) => {
      if (prev.length === 1) return prev; // keep at least one
      const newSteps = prev.filter((_, i) => i !== idx);
      // Adjust editor / preview indexes if needed
      if (editingRowIndex !== null) {
        if (editingRowIndex === idx) {
          setShowDetailEditor(false);
          setEditingRowIndex(null);
        } else if (editingRowIndex > idx) {
          setEditingRowIndex(editingRowIndex - 1);
        }
      }
      if (previewRowIndex !== null) {
        if (previewRowIndex === idx) {
          setPreviewRowIndex(null);
        } else if (previewRowIndex > idx) {
          setPreviewRowIndex(previewRowIndex - 1);
        }
      }
      // Sync top-level form with new first step
      const first = newSteps[0];
      setCampaignForm((prevForm) => ({
        ...prevForm,
        type: first.type,
        date: first.date,
        subject: first.subject,
        body: first.body,
      }));
      return newSteps;
    });
  };

  const availableTypesForRow = (idx) => {
    const usedOther = campaignSteps
      .filter((_, i) => i !== idx)
      .map((s) => s.type);
    return allowedTypes.filter((t) => !usedOther.includes(t));
  };

  const scrollCampaignsLeft = () => {
    if (campaignScrollRef.current) {
      campaignScrollRef.current.scrollBy({ left: -350, behavior: "smooth" });
    }
  };

  const scrollCampaignsRight = () => {
    if (campaignScrollRef.current) {
      campaignScrollRef.current.scrollBy({ left: 350, behavior: "smooth" });
    }
  };

  // Derived readiness for disabling submit button proactively
  // (Removed isCampaignFormReady to allow submit attempt even if incomplete)
  // const isCampaignFormReady = (() => Object.keys(computeErrors()).length === 0)();

  if (isLoading) {
    return (
      <div className="loader-container">
        <span className="loader" />
      </div>
    );
  }

  return (
    <>
      <div className="campaign-container">
        <div className="campaign-header-container ">
          <div className="campaign-title">On-Going Campaign</div>
          <button
            className="campaign-create-button"
            onClick={handleCreateCampaignClick}
          >
            {showCreateForm ? "Close Form" : "Create Campaign"}
          </button>
          <div className="empty-div"></div>
          {/* Google Sheets configuration button removed */}
        </div>

        {!companyProfileId && (
          <div className="campaign-empty-state" style={{ marginTop: 20 }}>
            {companyError || "Select or load a company profile first."}
          </div>
        )}

        {showCreateForm && (
          <div className="campaign-create-form">
            <div className="campaign-create-header-container">
              {" "}
              <div className="empty-div"></div>
              <div className="campaign-form-title">Create Campaign</div>{" "}
              <img
                width="48"
                height="48"
                src="https://img.icons8.com/fluency/48/delete-sign.png"
                alt="delete-sign"
                className="status-cross-symbol"
                onClick={handleCreateCampaignClick}
                style={{ cursor: "pointer" }}
              />
            </div>

            <form className="campaign-form" onSubmit={handleSubmitCampaign}>
              <div className="add-cutomer-input-container campaign-name-input">
                <label className="campaign-field-label">Campaign Name:</label>
                <input
                  type="text"
                  name="name"
                  className={`add-cutomer-input ${
                    fieldErrors.name ? "invalid" : ""
                  }`}
                  value={campaignForm.name}
                  onChange={(e) => {
                    handleFormChange(e);
                    if (fieldErrors.name)
                      setFieldErrors((prev) => {
                        const p = { ...prev };
                        delete p.name;
                        return p;
                      });
                  }}
                  required
                />
              </div>

              <div className="add-cutomer-input-container campaign-category-input">
                <label className="campaign-field-label">
                  Campaign Category:
                </label>
                <select
                  name="campaignCategory"
                  className="add-cutomer-input dropdown-input"
                  value={campaignForm.campaignCategory}
                  onChange={handleFormChange}
                >
                  <option value="Recurring">Recurring</option>
                  <option value="Special Event">Special Event</option>
                </select>
              </div>

              <div className="add-cutomer-input-container campaign-category-input">
                <label className="campaign-field-label">Target Audience:</label>
                <select
                  name="audience"
                  className="add-cutomer-input"
                  value={campaignForm.audience}
                  onChange={handleFormChange}
                >
                  <option value="Customers">Customers</option>
                  <option value="Leads">Leads</option>
                  <option value="All">All</option>
                </select>
              </div>
              {campaignForm.campaignCategory !== "Special Event" && (
                <>
                  <div className="add-cutomer-input-container">
                    <label className="campaign-field-label">Start Date:</label>
                    <input
                      type="date"
                      name="startDate"
                      className={`add-cutomer-input ${
                        fieldErrors.startDate || startDateInputError ? "invalid" : ""
                      }`}
                      value={campaignForm.startDate}
                      onChange={(e) => {
                        handleFormChange(e);
                        if (fieldErrors.startDate)
                          setFieldErrors((p) => {
                            const q = { ...p };
                            delete q.startDate;
                            return q;
                          });
                      }}
                      min={getTomorrowDate()}
                    />
                  </div>
                  <div className="add-cutomer-input-container">
                    <label className="campaign-field-label">End Date:</label>
                    <input
                      type="date"
                      name="endDate"
                      className={`add-cutomer-input ${
                        fieldErrors.endDate ? "invalid" : ""
                      }`}
                      value={campaignForm.endDate}
                      onChange={(e) => {
                        handleFormChange(e);
                        if (fieldErrors.endDate)
                          setFieldErrors((p) => {
                            const q = { ...p };
                            delete q.endDate;
                            return q;
                          });
                      }}
                      min={campaignForm.startDate ? getDateWithDaysAdded(campaignForm.startDate, 6) : getTomorrowDate()}
                      disabled={!campaignForm.startDate}
                    />
                  </div>
                </>
              )}
              <div className="create-campaign-row-header-container campaign-input-full-width">
                <label className="row-create-label campaign-col-1">Type</label>
                {campaignForm.campaignCategory === "Special Event" && (
                  <label className="row-create-label campaign-col-1">
                    Set Date
                  </label>
                )}
                <label className="row-create-label campaign-col-2">
                  Action
                </label>
                <label className="row-create-label campaign-col-3"> </label>
                <label className="row-create-label campaign-col-4"> </label>
              </div>
              {campaignSteps.map((step, idx) => (
                <div
                  key={idx}
                  className={(() => {
                    const positionClass =
                      campaignSteps.length === 1
                        ? "campaign-step-single"
                        : idx === 0
                        ? "campaign-step-first"
                        : idx === campaignSteps.length - 1
                        ? "campaign-step-last"
                        : "campaign-step-middle";
                    return `create-campaign-row-container campaign-input-full-width ${positionClass}`;
                  })()}
                >
                  <div className="row-add-cutomer-input-container ">
                    <select
                      className={`add-cutomer-input ${
                        fieldErrors[`step${idx}_type`] ? "invalid" : ""
                      }`}
                      value={step.type}
                      onChange={(e) => {
                        handleStepTypeChange(idx, e.target.value);
                        if (fieldErrors[`step${idx}_type`])
                          setFieldErrors((p) => {
                            const q = { ...p };
                            delete q[`step${idx}_type`];
                            return q;
                          });
                      }}
                    >
                      {availableTypesForRow(idx).map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  {campaignForm.campaignCategory === "Special Event" && (
                    <div className="row-add-cutomer-input-container">
                      <input
                        type="date"
                        className={`add-cutomer-input ${
                          fieldErrors[`step${idx}_date`] ? "invalid" : ""
                        }`}
                        value={step.date}
                        onChange={(e) => {
                          handleStepDateChange(idx, e.target.value);
                          if (fieldErrors[`step${idx}_date`])
                            setFieldErrors((p) => {
                              const q = { ...p };
                              delete q[`step${idx}_date`];
                              return q;
                            });
                        }}
                      />
                    </div>
                  )}
                  <div
                    className="campaign-add-detail-btn"
                    onClick={() => handleOpenDetailEditor(idx)}
                  >
                    {step.subject || step.body ? "Update Details" : "Add Details"}
                  </div>
                  <div
                    className="campaign-add-detail-btn row-view-btn"
                    onClick={() => {
                      console.log("View button clicked for idx:", idx);
                      console.log("Current previewRowIndex:", previewRowIndex);
                      console.log("Step data:", step);
                      console.log("Has subject:", !!step.subject);
                      console.log("Has body:", !!step.body);

                      // Ensure editor is closed before previewing
                      setShowDetailEditor(false);
                      setEditingRowIndex(null);

                      if (!step.subject && !step.body) {
                        toast.info("No details to preview. Please add details first.", {
                          className: "gradient-toast",
                          bodyClassName: "gradient-toast-body",
                          progressClassName: "gradient-toast-progress",
                        });
                        return;
                      }
                      if (previewRowIndex === idx) {
                        console.log("Closing preview for idx:", idx);
                        handleCloseDetailPreview();
                      } else {
                        console.log("Opening preview for idx:", idx);
                        handleOpenDetailPreview(idx);
                        // Scroll preview into view after render
                        setTimeout(() => {
                          const el = document.getElementById(`creation-preview-${idx}`);
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                        }, 60);
                      }
                    }}
                  >
                    {previewRowIndex === idx ? "Hide" : "View"} <img src={viewicn} className="view-icn-img" alt="" />
                  </div>
                  <div
                    className={(function () {
                      const isLast = idx === campaignSteps.length - 1;
                      const canAdd =
                        campaignForm.campaignCategory === "Special Event" &&
                        campaignSteps.length < allowedTypes.length;
                      if (isLast) {
                        return (
                          "campaign-add-row-btn" + (canAdd ? "" : " disabled")
                        );
                      }
                      return (
                        "campaign-add-row-btn" +
                        (campaignSteps.length === 1 ? " disabled" : "")
                      );
                    })()}
                    onClick={() => {
                      const isLast = idx === campaignSteps.length - 1;
                      const canAdd =
                        campaignForm.campaignCategory === "Special Event" &&
                        campaignSteps.length < allowedTypes.length;
                      if (isLast) {
                        if (canAdd) handleAddStep();
                      } else {
                        if (campaignSteps.length > 1) handleRemoveStep(idx);
                      }
                    }}
                    title={(function () {
                      const isLast = idx === campaignSteps.length - 1;
                      const canAdd =
                        campaignForm.campaignCategory === "Special Event" &&
                        campaignSteps.length < allowedTypes.length;
                      if (isLast) {
                        return canAdd
                          ? "Add next step"
                          : campaignForm.campaignCategory === "Recurring"
                          ? "Multiple steps disabled for Recurring"
                          : "All steps added";
                      }
                      return campaignSteps.length === 1
                        ? "Cannot remove the only step"
                        : "Remove this step";
                    })()}
                  >
                    {(() => {
                      const isLast = idx === campaignSteps.length - 1;
                      const canAdd =
                        campaignForm.campaignCategory === "Special Event" &&
                        campaignSteps.length < allowedTypes.length;
                      if (isLast) return "➕";
                      if (campaignSteps.length > 1) return "➖";
                      return "➕"; // single row initial state
                    })()}
                  </div>
                </div>
              ))}
              
              {/* Preview section for campaign creation form - only show when creating, not viewing details */}
              {showCreateForm && previewRowIndex !== null && campaignSteps[previewRowIndex] && (
                <div id={`creation-preview-${previewRowIndex}`} className="campaign-inline-preview" key={`preview-${previewRowIndex}`}>
                  <h4>Preview for {campaignSteps[previewRowIndex].type}</h4>
                  <div className="inline-preview-field">
                    <strong>Subject:</strong><br />
                    {campaignSteps[previewRowIndex].subject || <em>No subject added yet</em>}
                  </div>
                  <div className="inline-preview-body">
                    <strong>Body:</strong><br />
                    <div className="inline-preview-body-text">
                      {campaignSteps[previewRowIndex].body
                        ? campaignSteps[previewRowIndex].body
                            .split(/\n+/)
                            .map((ln, i) => (
                              <p key={i}>{ln}</p>
                            ))
                        : <em>No body content added yet</em>}
                    </div>
                  </div>
                  {campaignSteps[previewRowIndex].attachmentFile && (
                    <div className="inline-preview-attachment">
                      <strong>Attachment:</strong> {campaignSteps[previewRowIndex].attachmentFile.name}
                    </div>
                  )}
                </div>
              )}
       
              {showDetailEditor && editingRowIndex !== null && (
                <div className="campaign-detail-editor-overlay">
                  <div className="campaign-detail-editor-modal">
                    <div className="campaign-detail-editor-header">
                      <h3>
                        Add {campaignSteps[editingRowIndex]?.type} Details
                      </h3>
                      <span
                        className="campaign-detail-editor-close"
                        onClick={handleCancelDetailEditor}
                      >
                        ✕
                      </span>
                    </div>
                    <div className="campaign-body-subject-container popup">
                      <div className="add-cutomer-input-container campaign-input-full-width ">
                        <label className="campaign-field-label">
                          Email Subject:
                        </label>
                        <input
                          type="text"
                          className={`add-cutomer-input campaign-subject-input ${
                            fieldErrors[`step${editingRowIndex}_subject`]
                              ? "invalid"
                              : ""
                          }`}
                          value={editorForm.subject}
                          onChange={(e) => {
                            setEditorForm((f) => ({
                              ...f,
                              subject: e.target.value,
                            }));
                            if (fieldErrors[`step${editingRowIndex}_subject`])
                              setFieldErrors((p) => {
                                const q = { ...p };
                                delete q[`step${editingRowIndex}_subject`];
                                return q;
                              });
                          }}
                          required
                        />
                      </div>
                      <div className="add-cutomer-input-container campaign-input-full-width">
                        <label className="campaign-field-label">
                          Body Content:
                        </label>
                        <textarea
                          className={`add-cutomer-input campaign-body-textarea ${
                            fieldErrors[`step${editingRowIndex}_body`]
                              ? "invalid"
                              : ""
                          }`}
                          value={editorForm.body}
                          onChange={(e) => {
                            setEditorForm((f) => ({
                              ...f,
                              body: e.target.value,
                            }));
                            if (fieldErrors[`step${editingRowIndex}_body`])
                              setFieldErrors((p) => {
                                const q = { ...p };
                                delete q[`step${editingRowIndex}_body`];
                                return q;
                              });
                          }}
                        />
                      </div>
                      <div className="new-task-row-input">
                        <label
                          htmlFor="referenceimage"
                          className="campaign-field-label"
                        >
                          Attachment:
                        </label>
                        <input
                          type="file"
                          className="img-input campaign-attachment"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            if (editingRowIndex !== null) {
                              setCampaignSteps((prev) =>
                                prev.map((s, i) =>
                                  i === editingRowIndex
                                    ? { ...s, attachmentFile: file }
                                    : s
                                )
                              );
                            }
                          }}
                        />
                      </div>
                      <div className="campaign-detail-editor-actions">
                        <button
                          type="button"
                          className="submit-campaign-button"
                          onClick={handleSaveDetailEditor}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="submit-campaign-button cancel"
                          onClick={handleCancelDetailEditor}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="add-campaign-submit-button-container">
                <button
                  type="submit"
                  className="submit-campaign-button"
                  disabled={
                    campaignSubmitStatus === "submitting" ||
                    campaignSubmitStatus === "submitted"
                  }
                >
                  {campaignSubmitStatus === "submitting"
                    ? "Submitting..."
                    : campaignSubmitStatus === "submitted"
                    ? "Submitted"
                    : "Submit Campaign"}
                </button>
              </div>
            </form>
          </div>
        )}
        {!selectedCampaign && !showCreateForm && (
          <div className="ongoing-campaign-container">
            <div className="campaign-arrows">
              <img
                src={backicn}
                alt="scroll-left"
                className="assign-task-btn back campaign-nav-btn"
                onClick={campaigns.length > 4 ? scrollCampaignsLeft : undefined}
                style={{
                  visibility: campaigns.length > 4 ? 'visible' : 'hidden',
                  pointerEvents: campaigns.length > 4 ? 'auto' : 'none'
                }}
              />
              {campaigns.length > 0 ? (
                <div className="campaign-card-row" ref={campaignScrollRef}>
                  {campaigns.map((c) => (
                    <div
                      className="campaign-card"
                      key={c._id || c.id}
                      title={c.name}
                      onClick={() => {
                        setSelectedCampaign(c);
                        setPreviewRowIndex(null); // Reset preview when opening campaign details
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="campaign-name-wrapper">
                        <div className="campaign-name">{c.name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="temp-row-container tsk campaign-empty-state">
                  <div className="temp-title">
                    No campaigns yet – click above to create your first
                    campaign!
                  </div>
                  <div className="temp-img"></div>
                </div>
              )}
              <img
                src={nexticn}
                alt="scroll-right"
                className="assign-task-btn next campaign-nav-btn"
                onClick={campaigns.length > 4 ? scrollCampaignsRight : undefined}
                style={{
                  visibility: campaigns.length > 4 ? 'visible' : 'hidden',
                  pointerEvents: campaigns.length > 4 ? 'auto' : 'none'
                }}
              />
            </div>
          </div>
        )}

        {selectedCampaign &&
          !showCreateForm &&
          !showDetailEditor && // removed !showDetailPreview so page stays while previewing
          (
            <div className="campaign-detail-page-container">
              <div className="campaign-detail-page">
                {(() => {
                  const list =
                    selectedCampaign.campaignDetailsListArray ||
                    selectedCampaign.steps ||
                    [];
                  const first = list[0] || {};
                  selectedCampaign.__firstDetail = first;
                  return null;
                })()}
                <div className="campaign-detail-header-row">
                  <div className="empty-div"></div>
                  <div className="campaign-form-title">Campaign Details</div>
                  <img
                    width="48"
                    height="48"
                    src="https://img.icons8.com/fluency/48/delete-sign.png"
                    alt="delete-sign"
                    className="status-cross-symbol"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedCampaign(null);
                      setPreviewRowIndex(null); // Reset preview when closing campaign details
                    }}
                  />
                </div>
                <div className="campaign-detail-section">
                  <div className="campaign-first-detail-row">
                    <div className="campaign-name-set">
                      <div className="campaign-detail-name">Campaign Name:</div>
                      <div className="campaign-detail-name-data">
                        {selectedCampaign.name || "Unnamed Campaign"}
                      </div>
                    </div>
                    <div className="campaign-name-set">
                      <div className="campaign-detail-name">
                        Campaign Category:
                      </div>
                      <div className="campaign-detail-name-data">
                        {selectedCampaign.campaignCategory || "Unknown"}
                      </div>
                    </div>
                    <div className="campaign-name-set">
                      <div className="campaign-detail-name">
                        Target Audience:
                      </div>
                      <div className="campaign-detail-name-data">
                        {selectedCampaign.audience || "Not specified"}
                      </div>
                    </div>

                    {selectedCampaign.campaignCategory === "Recurring" && (
                      <>
                        <div className="campaign-name-set">
                          <div className="campaign-detail-name">
                            Start Date:
                          </div>
                          <div className="campaign-detail-name-data">
                            {selectedCampaign.startDate
                              ? new Date(
                                  selectedCampaign.startDate
                                ).toLocaleDateString()
                              : "Not set"}
                          </div>
                        </div>
                        <div className="campaign-name-set">
                          <div className="campaign-detail-name">End Date:</div>
                          <div className="campaign-detail-name-data">
                            {selectedCampaign.endDate
                              ? new Date(
                                  selectedCampaign.endDate
                                ).toLocaleDateString()
                              : "Not set"}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="campaign-detail-row-wrapper">
                    <div className="create-campaign-detail-row-header-container campaign-input-full-width">
                      <label className="row-create-label campaign-detail-col-1 campaign-detail-header">
                        Type
                      </label>
                      {selectedCampaign.campaignCategory ===
                        "Special Event" && (
                        <label className="row-create-label campaign-detail-col-2 campaign-detail-header">
                          Set Date
                        </label>
                      )}
                      <label className="row-create-label campaign-detail-col-3 campaign-detail-header">
                        Action
                      </label>
                    </div>

                    {(selectedCampaign.campaignDetailsListArray || []).map(
                      (step, stepIndex) => (
                        <React.Fragment key={stepIndex}>
                          <div className="campaign-detail-row-container">
                            <div className="campaign-name-set">
                              <div className="campaign-detail-type-data">
                                {step.type || "No type specified"}
                              </div>
                            </div>
                            {selectedCampaign.campaignCategory ===
                              "Special Event" && (
                              <div className="campaign-name-set">
                                <div className="campaign-detail-name-data">
                                  {step.date
                                    ? new Date(step.date).toLocaleDateString()
                                    : "Not set"}
                                </div>
                              </div>
                            )}
                            <div
                              className="campaign-add-detail-btn row-view-btn"
                              onClick={() => {
                                console.log("View button clicked for step:", stepIndex); // Debug log
                                console.log("Current previewRowIndex:", previewRowIndex); // Debug log
                                console.log("Step data:", step); // Debug log
                                previewRowIndex === stepIndex
                                  ? handleCloseDetailPreview()
                                  : handleOpenDetailPreview(stepIndex);
                              }}
                              title={
                                previewRowIndex === stepIndex
                                  ? "Hide details"
                                  : "View details"
                              }
                            >
                              {previewRowIndex === stepIndex ? "Hide" : "View"}{" "}
                              <img
                                src={viewicn}
                                className="view-icn-img"
                                alt=""
                              />
                            </div>
                          </div>

                          {previewRowIndex === stepIndex && (
                            <div className="campaign-inline-preview">
                              <div className="inline-preview-field">
                                <strong>Subject:</strong>{" "}
                                <br />
                                {step.subject || <em>No subject</em>}
                              </div>
                              <div className="inline-preview-body">
                                <strong>Body:</strong><br />
                                <div className="inline-preview-body-text">
                                  {step.body
                                    ? step.body
                                        .split(/\n+/)
                                        .map((ln, i) => (
                                          <p key={i}>{ln}</p>
                                        ))
                                    : <em>No body content</em>}
                                </div>
                              </div>
                              {step.attachmentUrl && (
                                <div className="inline-preview-attachment">
                                  <strong>Attachment:</strong>{" "}
                                  {/\.((jpe?g|png|gif|bmp|webp|svg))$/i.test(
                                    step.attachmentUrl
                                  ) ? (
                                    <img
                                      src={step.attachmentUrl}
                                      alt="Attachment"
                                      className="campaign-preview-image"
                                    />
                                  ) : (
                                    <a
                                      href={step.attachmentUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Open Attachment
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        transition={Zoom}
      />
    </>
  );
}
